import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode } from '@/editor/types';
import { ENTITY_RENDER_ORDER, hoverOutlineColor, selectedOutlineColor } from '@/editor/constant/entity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { modelManager } from '.';
import { EEntityStatus2 } from '@/editor/types/entity';
import SeparateSelectedEntity from '@/editor/entities/selectedEntity/separateSelectedEntity';
import RegionSelectedEntity from '@/editor/entities/selectedEntity/regionSelectedEntity';
import FaceSelectedEntity from '@/editor/entities/selectedEntity/faceSelectedEntity';
import {
  EFaceDataBaseType,
  IBaseDataArcEdgeFace,
  IBaseDataCylinderFace,
  IBaseDataEdgeFace,
  IFaceData
} from '@/editor/data/face/face';
import { EAutoLayoutMode } from '@/editor/utils/layout/detect';
import WallSelectedEntity from '@/editor/entities/selectedEntity/wallSelectedEntity';
import { getPathFromEdges } from '@/editor/utils/edge';
import BoxesSelectedEntity from '@/editor/entities/selectedEntity/boxesSelectedEntity';
import { getPureBoxes } from '@/editor/data/utils';
import { isMultiSelectDoorWindowOnly } from '@/editor/utils/select/multi-select';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { RoomData } from '@/editor/data/room/Room';
import { RegionData } from '@/editor/data/region/Region';
import WindowEntity from '@/editor/entities/windowEntity';
import SeparateWallEntity from '@/editor/entities/separateWallEntity';

// selected 高亮框和 hover 高亮框
export class SelectedHoveredManager {
  private readonly _ignoreResetSelectedOperateMode: readonly EOperateMode[] = [
    EOperateMode.EMPTY,
    EOperateMode.Alignment,
    EOperateMode.Array,
    EOperateMode.MultiSelect
  ];
  private _hoverRafId: number | null = null;
  private _selectedRafId: number | null = null;

  private _selectedBoxes: BoxesSelectedEntity;
  private _hoverBoxes: BoxesSelectedEntity;

  private _selectedEdgeFaceEntity: FaceSelectedEntity;
  private _hoverEdgeFaceEntity: FaceSelectedEntity;

  private _selectedRegionEntity: RegionSelectedEntity;
  private _hoverRegionEntity: RegionSelectedEntity;

  private _hoverSeparateWall: SeparateSelectedEntity;
  private _hoverWallEntity: WallSelectedEntity;

  private _selectedWindowEntities: WindowEntity[] = [];
  /** 多选中的隔墙不画包围盒，改为把墙体本身置成选中态，这里记下来好复位 */
  private _selectedSeparateWallEntities: SeparateWallEntity[] = [];

  private _measureSelectedBoxes: Map<string, BoxesSelectedEntity> = new Map();

  private _disposeFuncs: Set<() => void> = new Set();

  constructor() {
    this._selectedBoxes = new BoxesSelectedEntity();
    this._hoverBoxes = new BoxesSelectedEntity(ENTITY_RENDER_ORDER.hoverOutline);

    this._selectedEdgeFaceEntity = new FaceSelectedEntity(selectedOutlineColor);
    this._hoverEdgeFaceEntity = new FaceSelectedEntity(hoverOutlineColor);

    this._hoverWallEntity = new WallSelectedEntity({ color: hoverOutlineColor, fill: false });
    this._hoverSeparateWall = new SeparateSelectedEntity({});

    this._selectedRegionEntity = new RegionSelectedEntity(selectedOutlineColor);
    this._hoverRegionEntity = new RegionSelectedEntity(hoverOutlineColor);

    this._disposeFuncs
      .add(editorEvent.on(EditorEventName.ENTITY_SELECTED, this.updateSelected))
      .add(editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this.updateSelected))
      .add(globalStores.editorStateStore.onChange('hoverEntity', this.updateHover))
      .add(globalStores.editorStateStore.onChange('moving', this.updateSelected));
  }

  private updatePrevSelected() {
    const { preSelected, selected: curSelect } = globalStores.editorStateStore;

    if (preSelected.entityId === curSelect.entityId && preSelected.type === curSelect.type) return;

    switch (preSelected.type) {
      case EEntityType.MultiModel: {
        for (const windowEntity of this._selectedWindowEntities) {
          windowEntity.selected = false;
        }
        this._selectedWindowEntities = [];

        for (const sepWallEntity of this._selectedSeparateWallEntities) {
          sepWallEntity.selected = false;
        }
        this._selectedSeparateWallEntities = [];
        break;
      }
      case EEntityType.DoorWindow: {
        const windowEntity = modelManager.windowManager.getEntity(preSelected.entityId);
        if (windowEntity) windowEntity.selected = false;
        break;
      }
      case EEntityType.Column: {
        const columnEntity = modelManager.structManager.getColumnEntity(preSelected.entityId);
        if (columnEntity) columnEntity.selected = false;
        break;
      }
      case EEntityType.Wall: {
        const wallEntity = modelManager.wallManager.getWallEntity(preSelected.entityId);
        if (wallEntity) wallEntity.selected = false;
        break;
      }
      case EEntityType.SeparateWall: {
        const sepWallEntity = modelManager.separateWallManager.getEntity(preSelected.entityId);
        if (sepWallEntity) sepWallEntity.selected = false;
        break;
      }
      case EEntityType.AreaDimension:
      case EEntityType.LinearDimension:
        modelManager.dimensionManager.updateSelected(preSelected.entityId, false);
        break;
    }
  }

  updateSelected = () => {
    this.updatePrevSelected();

    const selected = globalStores.editorStateStore.selected;
    const operateInfo = globalStores.operateInfo;

    if (!this._ignoreResetSelectedOperateMode.includes(operateInfo.mode)) {
      this.clearSelected();
      this.clearHover();
      return;
    }

    if (selected.type === EEntityType.EMPTY) {
      this.clearSelected();
      // 当前没有选中构件时退出对齐模式
      if (operateInfo.mode === EOperateMode.Alignment) globalStores.alignOperation.toggleAlignModeEnabled(false);
      return;
    }

    if (globalStores.editorStateStore.moving) {
      this.clearSelected();
      return;
    }

    const isAutoLayoutMode = globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE;
    if (isAutoLayoutMode) {
      // 布局模式下不显示 selected 效果
      this.clearSelected();
      this.clearHover();
      return;
    }

    this.clearHover();

    const data = globalStores.getDataByIdType(selected.entityId, selected.type);
    if (!data) return;

    let boxesData: ReturnType<typeof getPureBoxes>;

    switch (selected.type) {
      case EEntityType.Wall: {
        const wallEntity = modelManager.wallManager.getWallEntity(selected.entityId);
        if (wallEntity) wallEntity.selected = true;
        break;
      }
      case EEntityType.Furniture:
      case EEntityType.ModelGroup:
      case EEntityType.Assembly:
      case EEntityType.MultiModel:
        {
          // 门窗特殊处理
          if (selected.type === EEntityType.MultiModel && isMultiSelectDoorWindowOnly(data as IMultiModelData)) {
            this._selectedWindowEntities = [];
            for (const modelInfo of (data as IMultiModelData).modelInfos) {
              const windowEntity = modelManager.windowManager.getEntity(modelInfo.id);
              if (!windowEntity) continue;
              this._selectedWindowEntities.push(windowEntity);
              windowEntity.selected = true;
            }
          }
          // 隔墙特殊处理：轴对齐包围盒对斜墙/弧墙太虚，改为高亮墙体本身，getPureBoxes 里不会出它的子盒
          if (selected.type === EEntityType.MultiModel) {
            this._selectedSeparateWallEntities = [];
            for (const modelInfo of (data as IMultiModelData).modelInfos) {
              if (modelInfo.type !== EEntityType.SeparateWall) continue;
              const sepWallEntity = modelManager.separateWallManager.getEntity(modelInfo.id);
              if (!sepWallEntity) continue;
              this._selectedSeparateWallEntities.push(sepWallEntity);
              sepWallEntity.selected = true;
            }
          }
          boxesData = getPureBoxes(selected);
        }
        break;
      case EEntityType.DoorWindow:
        {
          const windowEntity = modelManager.windowManager.getEntity(selected.entityId);
          if (windowEntity) {
            this._selectedWindowEntities = [windowEntity];
            windowEntity.selected = true;
            boxesData = getPureBoxes(selected);
          }
        }
        break;
      case EEntityType.Face:
        {
          const faceData = data as IFaceData;
          if (
            faceData.baseDataType === EFaceDataBaseType.Edge ||
            faceData.baseDataType === EFaceDataBaseType.Cylinder ||
            faceData.baseDataType === EFaceDataBaseType.ArcEdge
          ) {
            this._selectedEdgeFaceEntity.update(
              faceData.baseDataType,
              faceData.baseData as IBaseDataEdgeFace | IBaseDataCylinderFace | IBaseDataArcEdgeFace
            );
          }
        }
        break;
      case EEntityType.Room: {
        const roomData = data as RoomData;
        this._selectedRegionEntity.update({
          contour: getPathFromEdges(roomData.pathInfos),
          holes: roomData.holeInfos.map((holes) => getPathFromEdges(holes)),
          color: selectedOutlineColor,
          offGround: roomData.offGround
        });
        break;
      }
      case EEntityType.Region:
        {
          const regionData = data as RegionData;
          this._selectedRegionEntity.update({
            contour: getPathFromEdges(regionData.corePathInfos),
            holes: regionData.coreHoleInfos.map((holes) => getPathFromEdges(holes)),
            color: selectedOutlineColor,
            offGround: regionData.offGround
          });
        }
        break;
      case EEntityType.SeparateWall: {
        const sepWallEntity = modelManager.separateWallManager.getEntity(selected.entityId);
        if (sepWallEntity) sepWallEntity.selected = true;
        break;
      }
      case EEntityType.Column: {
        const columnEntity = modelManager.structManager.getColumnEntity(selected.entityId);
        if (columnEntity) columnEntity.selected = true;
        break;
      }
      case EEntityType.AreaDimension:
      case EEntityType.LinearDimension:
        modelManager.dimensionManager.updateSelected(selected.entityId, true);
        break;
    }

    this.clearSelected(selected.type);

    if (!boxesData) return;
    this._selectedBoxes.create({ ...boxesData, linewidth: 1, color: selectedOutlineColor });
  };

  /** 更新量尺功能选中 */
  updateMeasureSelected(measureEntities: { type: EEntityType; id: string }[]) {
    this.clearHover();
    for (const [entityId, boxes] of this._measureSelectedBoxes.entries()) {
      const measureEntity = measureEntities.find(({ id }) => id === entityId);
      if (!measureEntity) {
        boxes.clear();
        this._measureSelectedBoxes.delete(entityId);
        continue;
      }
    }

    for (const { id: entityId, type } of measureEntities) {
      const boxesEntity = this._measureSelectedBoxes.get(entityId);
      boxesEntity?.clear();
      if (type === EEntityType.Furniture || type === EEntityType.Assembly) {
        const newBoxesEntity = boxesEntity ?? new BoxesSelectedEntity();
        const boxesData = getPureBoxes({ type, entityId });
        if (boxesData) {
          newBoxesEntity.create({ ...boxesData, linewidth: 1, color: selectedOutlineColor });
          this._measureSelectedBoxes.set(entityId, newBoxesEntity);
        } else {
          this._measureSelectedBoxes.delete(entityId);
        }
      } else {
        this._measureSelectedBoxes.delete(entityId);
      }
    }
  }

  // 需要在move时直接调用，使用监听再更新位置，会有明显延迟，效果不好
  updateSelectedPositionAndRotation = (position: IPoint2d, rotation?: number) => {
    const selected = globalStores.editorStateStore.selected;
    const moving = globalStores.editorStateStore.moving;

    if (
      selected.type === EEntityType.EMPTY ||
      ![EEntityType.Furniture, EEntityType.Assembly, EEntityType.MultiModel].includes(selected.type) ||
      !moving
    ) {
      this._selectedBoxes.clear();
      return;
    }

    if (!this._selectedBoxes.created) {
      this.updateSelected();
      return;
    }

    this._selectedBoxes.update({ x: position.x, y: position.y, rotation: rotation || 0 });
  };

  updateHover = () => {
    const hover = globalStores.editorStateStore.hoverEntity;
    const selected = globalStores.editorStateStore.selected;

    if (!hover.entityId || hover.entityId === selected.entityId) {
      this.clearHover();
      return;
    }

    const isLayoutMode = globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE;
    const allowedRegion = globalStores.editorStateStore.autoLayoutModeData.allowedRoomIds;
    if (isLayoutMode && (!allowedRegion.length || !allowedRegion.includes(hover.entityId))) {
      this.clearHover();
      return;
    }

    // let boxParams; // 3d选中框参数
    let boxesData: ReturnType<typeof getPureBoxes>;

    switch (hover.type) {
      case EEntityType.Wall:
        {
          const wallData = globalStores.wallStore.getWallData(hover.entityId);
          if (wallData) {
            this._hoverWallEntity.update(wallData.drawWallInfo.drawShape[0]);
          }
        }
        break;
      case EEntityType.Furniture:
      case EEntityType.ModelGroup:
      case EEntityType.Assembly:
        boxesData = getPureBoxes(hover);
        break;
      case EEntityType.DoorWindow:
        if (!this._selectedWindowEntities.map((entity) => entity.id).includes(hover.entityId)) {
          boxesData = getPureBoxes(hover);
        }
        break;
      case EEntityType.Face: {
        const faceData = globalStores.faceStore.getFaceData(hover.entityId);
        if (
          faceData &&
          (faceData.baseDataType === EFaceDataBaseType.Edge ||
            faceData.baseDataType === EFaceDataBaseType.Cylinder ||
            faceData.baseDataType === EFaceDataBaseType.ArcEdge)
        ) {
          this._hoverEdgeFaceEntity.update(
            faceData.baseDataType,
            faceData.baseData as IBaseDataEdgeFace | IBaseDataCylinderFace | IBaseDataArcEdgeFace
          );
        }

        break;
      }
      case EEntityType.Room: {
        const roomData = globalStores.roomStore.getRoomData(hover.entityId);
        if (roomData) {
          this._hoverRegionEntity.update({
            contour: getPathFromEdges(roomData.pathInfos),
            holes: roomData.holeInfos.map((holes) => getPathFromEdges(holes)),
            color: hoverOutlineColor,
            offGround: roomData.offGround
          });
        }
        break;
      }
      case EEntityType.Region: {
        const regionData = globalStores.regionStore.getRegionData(hover.entityId);
        if (regionData) {
          this._hoverRegionEntity.update({
            contour: getPathFromEdges(regionData.corePathInfos),
            holes: regionData.coreHoleInfos.map((holes) => getPathFromEdges(holes)),
            color: hoverOutlineColor,
            offGround: regionData.offGround
          });
        }
        break;
      }
      case EEntityType.SeparateWall: {
        // 已在多选里的隔墙保持选中态，hover 覆盖层会整个盖掉它（与上面门窗的处理同理）
        if (this._selectedSeparateWallEntities.some((entity) => entity.id === hover.entityId)) {
          this.clearHover();
          return;
        }

        const separateWallData = globalStores.getDataByIdType(hover.entityId, EEntityType.SeparateWall);
        if (separateWallData) {
          this._hoverSeparateWall.update({
            ...separateWallData,
            status: EEntityStatus2.Hover,
            forceUpdate: true
          });
        }
        break;
      }
    }

    this.clearHover(hover.type);

    if (!boxesData) return;

    this._hoverRafId = requestAnimationFrame(() => {
      this._hoverRafId = null;
      this._hoverBoxes.create({
        ...boxesData,
        linewidth: 1,
        color: hoverOutlineColor
      });
    });
  };

  clearHover(currentHoverEntityType?: EEntityType) {
    let exclude: unknown;

    switch (currentHoverEntityType) {
      case EEntityType.SeparateWall:
        exclude = this._hoverSeparateWall;
        break;
      case EEntityType.Furniture:
      case EEntityType.DoorWindow:
      case EEntityType.ModelGroup:
      case EEntityType.Assembly:
        exclude = this._hoverBoxes;
        break;
      case EEntityType.Face:
        exclude = this._hoverEdgeFaceEntity;
        break;
      case EEntityType.Room:
      case EEntityType.Region:
        exclude = this._hoverRegionEntity;
        break;
      case EEntityType.Wall:
        exclude = this._hoverWallEntity;
        break;
    }

    [
      this._hoverBoxes,
      this._hoverEdgeFaceEntity,
      this._hoverRegionEntity,
      this._hoverSeparateWall,
      this._hoverWallEntity
    ].forEach((entity) => {
      if (entity !== exclude) entity.clear();
    });

    if (this._hoverRafId) {
      cancelAnimationFrame(this._hoverRafId);
      this._hoverRafId = null;
    }
  }

  clearSelected(currentSelectedEntityType?: EEntityType) {
    let exclude: unknown;

    switch (currentSelectedEntityType) {
      case EEntityType.Furniture:
      case EEntityType.DoorWindow:
      case EEntityType.ModelGroup:
      case EEntityType.Assembly:
        exclude = this._selectedBoxes;
        break;
      case EEntityType.Face:
        exclude = this._selectedEdgeFaceEntity;
        break;
      case EEntityType.Region:
      case EEntityType.Room:
        exclude = this._selectedRegionEntity;
        break;
    }

    [this._selectedBoxes, this._selectedEdgeFaceEntity, this._selectedRegionEntity].forEach((entity) => {
      if (entity !== exclude) entity.clear();
    });
    if (this._selectedRafId) {
      cancelAnimationFrame(this._selectedRafId);
      this._selectedRafId = null;
    }
  }

  clearMeasureSelected() {
    for (const boxes of this._measureSelectedBoxes.values()) {
      boxes.clear();
    }
    this._measureSelectedBoxes.clear();
  }

  dispose() {
    this._disposeFuncs.forEach((dispose) => dispose());
    this._disposeFuncs.clear();
    this.clearMeasureSelected();
  }
}
